<template>
  <div class="field-consumption">
    <div class="panelheader">
      <div class="title">
        Power Consumption
      </div>
    </div>
    <div class="body">

      <el-row>
        <el-col :xs="5" :sm="5" :md="3" :lg="3">
          <total-consumption :data="totalConsumption"/>
        </el-col>
        <el-col :xs="5" :sm="5" :md="3" :lg="3">
          <div class="avg-info">
            <el-row>
              <el-col :span="5"> <i class="el-icon-fa-bolt icon-large"/></el-col>
              <el-col :span="19"><div class="consumption">
              {{ totalAvgConsumption }}kWh
            </div></el-col>
            </el-row>
            <div class="clear"></div>
            <div class="c-label">Total Average Usage</div>
          </div>
        </el-col>
        <el-col :xs="14" :sm="14" :md="18" :lg="18">
          <avgConsumption :data="avgSeries"/>
        </el-col>
      </el-row>

      <highstock :options="options" ref="stockChart" v-loading="chartLoading"/>
    </div>
  </div>
</template>

<script>
import requests from "../../requests";
import auth from '../../auth'
import dateFormat from 'dateformat'

export default {
  props: {
    displayFields: Array,
    displayClubs: Array,
    dateRange: Object
  },
  data() {
    return {
      fieldData: {},
      filteredData: {},
      avgData: {},
      totalAvgConsumption: 0,
      totalConsumption: 0,
      totalData: {},
      data: {},
      avgSeries: {},
      chartLoading: true,
      options: {
        credits: {
          enabled: false
        },
        chart: {
          height: 600
        },
        rangeSelector: {
          enabled: false
        },
        title: {
          text: null
        },
        yAxis: {
          title: {
            text: "Consumption"
          },
          max: 110,
          min: 0
        },
        tooltip: {
          enabled: true,
          formatter: function () {
            // Format the tooltip, eg:

            //  Monday, 11 Jun, 2018
            //  ○ Malburg Thunder Cricket | Field C: 99.9
            //  ○ No Club | Field D: 99.9

            const date = `
              <span style="font-size: 0.9em">
                ${dateFormat(this.x, 'dddd, dd mmm, yyyy')}
              </span>
              <br/>
            `

            const formatStr = date + this.points.reduce((str, point, i) => {
              const club_name = point.series.userOptions.club_name[i]
              const colouredDot = `<span style="color: ${point.color}">\u25CF</span>`
              const pointStr = `${colouredDot} ${club_name || 'No Club'} | ${point.series.name}: <b>${point.y}</b>`
              str += `${pointStr} <br/>`
              return str
            }, '')

            return formatStr
          }
        },
        xAxis: {
          type: "datetime",
          dateTimeLabelFormats: {
            millisecond: "%H:%M:%S.%L",
            second: "%H:%M:%S",
            minute: "%H:%M",
            hour: "%H:%M",
            day: "%e. %b",
            week: "%e. %b",
            month: "%b '%y",
            year: "%Y"
          },
          title: {
            text: "Date"
          },
          range: 6 * 30 * 24 * 3600 * 1000
        },
        series: []
      }
    };
  },
  beforeMount() {
    const profile = JSON.parse(auth.getProfile())

    if (profile.role !== 3) {
      this.getClientConsumption(profile.client_id).then(() => {
        this.chartLoading = false
      })
    } else {
      // We only have two clients atm and I'm lazy, sorry
      // TODO
      this.getClientConsumption(2).then(() => {
        this.chartLoading = false
        this.getClientConsumption(3)
      })
    }
  },
  mounted() {
    this.Highcharts.setOptions({
      global: {
        useUTC: false
      },
      credits: {
        enabled: false
      },
      time: {
        timezoneOffset: 6 * 60 // 10 hours
      }
    });
  },
  methods: {
    getClientConsumption(client_id) {
      return new Promise((resolve, reject) => {
          requests.getClientConsumption(this, client_id, result => {
            if (result.action === "success" && result.data) {
              let allData = result.data;

            // Turn a big list of data for all fields, into an object split up by field id
            // Concat it with previous, so we can do multiple for superadmins
            this.fieldData = {
              ...this.fieldData,
              ...allData.reduce((field_logs, log) => {
                const id = log.field_id;

                field_logs = field_logs || {};
                field_logs[id] = field_logs[id] || [];

                field_logs[id].push(log);

                return field_logs;
              }, {})
            };

            this.filterData(this.displayFields);
            resolve()
          }
        })
      })
    },
    filterData(displayFields) {
      if (!displayFields || !displayFields.length) {
        this.filteredData = {
          0: [
            { consumption: 0, logged: Date.now(), field: "No fields selected" }
          ]
        };
      } else {
        // filter out only the wanted fields, make it back into an object
        this.filteredData = Object.entries(this.fieldData)
          .filter(([field_id,]) => displayFields.find(display_id => display_id == field_id)) // Only the wanted fields
          .reduce((obj, [field_id, logs]) => {
            logs = logs.filter(log => this.dateInRange(log.logged, this.dateRange)); // In daterange

            if (this.displayClubs.length) {
              logs = logs.filter(log => this.displayClubs.indexOf(log.club_name) !== -1) // Only clubs in displayClubs, if specified
            }

            obj[field_id] = logs;
            return obj;
          }, {});
      }

      this.data = Object.entries(this.filteredData).reduce((arr, v, i) => {
        const v1 = v[1].map(u => [new Date(u.logged).getTime(), u.consumption]);

        let max = Math.max(...v1.map(u => u[0])); // Get the latest
        let min = Math.min(...v1.map(u => u[0])); // and earliest dates

        if (Math.abs(max) == Infinity || Math.abs(min) == Infinity) {
          max = Date.now();
          min = Date.now() - 24 * 60 * 60 * 1000;
        }

        let new_v1 = v1;

        if (v[1].length) {
          const newData = {
            id: v[0],
            name: v[1][0].field,
            club_name: v[1].map(data => data.club_name),
            data: new_v1
          };
          arr.push(newData);
        }
        return arr;
      }, []);

      this.options.series = this.data;

      //caculate avg consumption and map it to avgData array
      this.avgData = Object.entries(this.data)
        .map(v => v[1].data)
        .reduce((a, v) => {
          const all = v.map(u => u[1]);
          const total = all.reduce(function(a, b) {
            return a + b;
          }, 0);
          var avg = total / v.length;
          a.push(avg);
          return a;
        }, []);

      this.avgSeries = Object.entries(this.data)
        .map(v => v[1].data)
        .reduce((a, v, i) => {
          const all = v.map(u => u[1]);
          const total = all.reduce(function(a, b) {
            return a + b;
          }, 0);
          var avg = total / v.length;
          var field = Object.entries(this.data).map(v => v[1].name);
          const o = {
            data: [avg],
            name: field[i]
          };
          a.push(o);
          return a;
        }, []);

      this.totalData = this.data
        .map(v => v.data)
        .reduce((totals, v) => {
          const all = v.map(u => u[1]);
          const total = all.reduce((a, b) => a + b, 0);
          totals.push(total);
          return totals;
        }, []);

      this.totalConsumption = this.totalData
        .reduce((a, b) => a + b, 0)
        .toFixed(2);

      // this.doReloadChart()
      //caculate total avg consumption and put it in totalAvgConsumption
      this.totalAvgConsumption = (
        this.avgData.reduce((a, b) => a + b, 0) / this.avgData.length
      ).toFixed(2);

      if (isNaN(this.totalAvgConsumption)) {
        this.totalAvgConsumption = 0;
      }
    },
    dateInRange(date, range) {
      if (!range || !range.to || !range.from) {
        const one_day = 24 * 60 * 60 * 1000;
        range = { from: Date.now() - one_day, to: Date.now() };
      }
      range = { from: new Date(range.from), to: new Date(range.to) };
      date = new Date(date);

      return date >= range.from && date <= range.to;
    },
    doReloadChart() {
      this.reloadChart = true;
      this.$nextTick(() => {
        this.reloadChart = false;
      });
    },
    range(start, stop, step = 1) {
      return Array(Math.round((stop - start) / step))
        .fill(start)
        .map((x, y) => x + y * step);
    }
  },
  watch: {
    displayFields(newVal) {
      this.filterData(newVal);
    },
    displayClubs(newVal) {
      this.filterData(this.displayFields);
    },
    dateRange(newVal) {
      this.filterData(this.displayFields);
    }
  },
  components: {
    AvgConsumption: () =>
      import(/* webpackChunkName: "AvgConsumption" */ "./AvgConsumption"),
    TotalConsumption: () =>
      import(/* webpackChunkName: "TotalConsumption" */ "./TotalConsumption")
  }
};
</script>

<style>
i.el-icon-fa-bolt::before {
  width: 100%;
  height: 100%;
  color: #79bfcd;
  margin: 0;
  font-size: 3em;
}

.consumption {
  margin-top: 8pt;
  font-size: 2em;
  margin-left: 3pt;
  text-align: left;
}

.c-label {
  color: #888888;
  text-align: center;
}

.avg-info {
  margin-top: 60px;
  margin-left: 10px;
}
</style>



// WEBPACK FOOTER //
// FieldsConsumption.vue?1c501d32